import { FormattedMessage } from "react-intl";
import { useEcgDetailsContext } from "../../../../context/EcgDetailsContext";
import EcgAction from "../../EcgEdit/Actions/EcgAction/EcgAction";
import MetaCard from "../MetaCard/MetaCard";
import styles from "./EcgDraft.module.css";
import savedIcon from "../../../../assets/shared/success.svg";
import { IFindings } from "../../../../models/ecg.interface";
import { getCheckedLeads } from "../../../../utils/ecgUtil";
import { IDynamicData } from "../../../../models/dynamic-data.interface";
import TextDirAligner from "../../../Shared/TextDirAligner/TextDirAligner";

export const filterBoolean = (data: IDynamicData) => data.value;

const EcgDraft = () => {
  const {
    state: { saveFailed, draft, ecgEdit, selectedEcg },
    actions: { isEdited },
  } = useEcgDetailsContext();

  const draftDisabled =
    !isEdited({ includeUrgent: false }) &&
    (selectedEcg?.permissions?.enableActions ||
      selectedEcg?.permissions?.enableFindings ||
      selectedEcg?.permissions?.enableInterpretation ||
      selectedEcg?.permissions?.enablePriority);

  if (draftDisabled) {
    return null;
  }

  const findings = ecgEdit?.findings?.filter(filterBoolean);
  const actions = ecgEdit?.actions?.filter(filterBoolean);

  const draftMessage = saveFailed ? (
    <span className="prim-bg-color p-1 rounded">
      <FormattedMessage
        id="ECG_DETAILS.DRAFT.SAVE_FAILURE"
        defaultMessage="Your data did not save, try again"
      />
    </span>
  ) : draft.mode === "edit" ? (
    <FormattedMessage
      id="ECG_DETAILS.DRAFT.NOT_SAVED_YET"
      defaultMessage="Not yet saved"
    />
  ) : (
    <div className="d-flex">
      <img className={styles.savedIcon} src={savedIcon} alt="" />
      <span className="mx-sm"></span>
      <FormattedMessage id="ECG_DETAILS.DRAFT.SAVED" defaultMessage="Saved" />
    </div>
  );

  const renderLeads = (finding: IFindings) => {
    const checkedLeads = getCheckedLeads(finding.leads);
    if (!(finding.useLeads && checkedLeads.length)) return null;

    const leadsText =
      checkedLeads
        ?.map((lead) => lead.name)
        .join(", ")
        .replace(/, ([^,]*)$/, ", $1") ?? "";

    return (
      <div className={`${styles.leads} d-inline-block`}>
        <span className="mx-1">{leadsText}</span>
      </div>
    );
  };

  return (
    <MetaCard
      className={`font-md ${styles.card} ${
        draft.mode === "edit" ? styles.edit : ""
      }`}
    >
      <div className="d-flex justify-content-between mb-2">
        <p className="mb-0 prim-color-dark font-weight-bold">
          {draft.mode === "edit" ? (
            <i>
              -
              <FormattedMessage
                id="ECG_DETAILS.DRAFT.EDIT_TITLE"
                defaultMessage="Draft Report"
              />
              -
            </i>
          ) : (
            <FormattedMessage
              id="ECG_DETAILS.DRAFT.TITLE"
              defaultMessage="Report"
            />
          )}
        </p>
        <p className="mb-0">{draftMessage}</p>
      </div>
      <div
        className={`${styles.content} ${
          draft.mode === "edit" ? styles.edit : ""
        } p-2`}
      >
        {/* Findings */}
        {selectedEcg?.permissions?.enableFindings && (
          <section className="mb-3">
            <p className="mb-1">
              <strong
                className={`${
                  findings && findings.length
                    ? "font-weight-bold"
                    : "font-italic muted-color"
                }`}
              >
                <FormattedMessage
                  id="ECG_DETAILS.FINDINGS"
                  defaultMessage="Findings"
                />
              </strong>
            </p>
            <ul className="px-3 m-0">
              {findings?.map((f) => (
                <li className="mb-1" key={f.key}>
                  {f.displayName}
                  {f.useLeads && ":"}
                  {renderLeads(f)}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Interpretations */}
        {selectedEcg?.permissions?.enableInterpretation && (
          <section className="mb-3">
            <p className="mb-1">
              <strong
                className={`${
                  ecgEdit?.interpretation
                    ? "font-weight-bold"
                    : "font-italic muted-color"
                }`}
              >
                <FormattedMessage
                  id="ECG_DETAILS.INTERPRETATION"
                  defaultMessage="Interpretation"
                />
              </strong>
            </p>
            <TextDirAligner className="mb-0">
              {ecgEdit?.interpretation}
            </TextDirAligner>
          </section>
        )}

        {/* Actions */}
        {selectedEcg?.permissions?.enableActions && (
          <section className="mb-3">
            <p className="mb-1">
              <strong
                className={`${
                  actions && actions.length
                    ? "font-weight-bold"
                    : "font-italic muted-color"
                }`}
              >
                <FormattedMessage
                  id="ECG_DETAILS.ACTION_REQUIRED"
                  defaultMessage="Action Required"
                />
              </strong>
            </p>
            {actions?.map((action) => (
              <EcgAction
                key={action.key}
                severity={action.severity}
                displayName={action.displayName}
                smallIcon
                displaySeverity
              />
            ))}
          </section>
        )}
      </div>
    </MetaCard>
  );
};

export default EcgDraft;
